import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { faArrowRightArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowRightArrowLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppointmentListItem } from '@spike/appointments-list-model';
import KanbanColumnSelection from 'components/Bookings2/Daycare/Kanban/KanbanColumnSelection';
import KanbanMoveDestinationMenu from 'components/Bookings2/Daycare/Kanban/KanbanMoveDestinationMenu';
import KanbanRoomUnitsFilter from 'components/Bookings2/Daycare/Kanban/KanbanRoomUnitsFilter';
import classes from 'components/Bookings2/Daycare/Kanban/KanbanBookings.module.scss';
import {
    groupDaycareAppointments,
    KanbanRoomColumn,
    KanbanUnitGroup
} from 'components/Bookings2/Daycare/Kanban/groupDaycareAppointments';
import { fetchOrganizationServicesInfoThunk } from '@spike/marketplace-action';
import { PageResult } from '@spike/model';
import { Room } from '@spike/room-model';
import { useApiClientWrapper } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { KanbanAppointmentCard } from 'components/UI/V2/KanbanBoard/AppointmentCard/KanbanAppointmentCard';
import { KanbanBoardCard } from 'components/UI/V2/KanbanBoard/Card/KanbanBoardCard';
import { KanbanBoardColumn } from 'components/UI/V2/KanbanBoard/Column/KanbanBoardColumn';
import { KanbanBoardEmptyColumn } from 'components/UI/V2/KanbanBoard/EmptyColumn/KanbanBoardEmptyColumn';
import { KanbanBoard, KanbanCardMoveEvent } from 'components/UI/V2/KanbanBoard/KanbanBoard';
import { KanbanBoardRow } from 'components/UI/V2/KanbanBoard/Row/KanbanBoardRow';
import Spinner from 'components/UI/V3/Spinner';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const DAYCARE_KANBAN_SCROLLER_ID = 'daycare-kanban-board';

interface KanbanBookingsProps {
    appointments: Array<AppointmentListItem>;
    occupancyAppointments?: Array<AppointmentListItem>;
    selectedMarketplaceIds: Array<number>;
    loading?: boolean;
    selectedIds: Set<number>;
    selectableRoomIds: Array<number>;
    selectedRoomUnitIds: Array<number>;
    onChangeRoomUnits: (roomUnitIds: Array<number>, all: boolean) => void;
    onToggleSelect: (appointmentId: number, checked: boolean) => void;
    onToggleSelectAll: (checked: boolean, appointmentIds: Array<number>) => void;
    onMoveUnit: (appointmentIds: Array<number>) => void;
    onDropAppointments: (appointmentIds: Array<number>, roomUnitId: number | null, label: string) => void;
    onSelectMoveDestination: (roomUnitId: number | null, label: string) => void;
    onShowOrder: (bookingId: number, orderId?: number) => void;
    onShowPet: (petId: number) => void;
}

const visibleAppointments = (appointments: Array<AppointmentListItem>, statusId: number | null) =>
    statusId == null ? appointments : appointments.filter(appointment => appointment.status.id === statusId);

const currentUnitId = (appointment: AppointmentListItem) => appointment.room?.unit?.id ?? null;

const unitDestination = (unitId: number, columns: Array<KanbanRoomColumn>, rooms: Array<Room>) => {
    const column = columns.find(item => item.roomId != null && item.units.some(unit => unit.unitId === unitId));
    const unit = column?.units.find(item => item.unitId === unitId);
    const room = rooms.find(item => item.units.some(roomUnit => roomUnit.id === unitId));
    const roomUnit = room?.units.find(item => item.id === unitId);
    const roomName = room?.name ?? column?.title;
    const unitName = roomUnit?.name ?? unit?.title;
    if (!roomName || !unitName) {
        return null;
    }

    return { roomUnitId: unitId, label: `${roomName} (${unitName})` };
};

const destinationFor = (
    to: KanbanCardMoveEvent['to'],
    columns: Array<KanbanRoomColumn>,
    rooms: Array<Room>,
    unitsInColumn: (column: KanbanRoomColumn) => Array<KanbanUnitGroup>
) => {
    const unitMatch = to.rowId?.match(/^unit-(\d+)$/);
    if (unitMatch) {
        return unitDestination(Number(unitMatch[1]), columns, rooms);
    }

    const column = columns.find(item => item.key === to.columnId);
    if (!column || column.roomId == null) {
        return column ? { roomUnitId: null as number | null, label: 'Unassigned' } : null;
    }

    const units = unitsInColumn(column);
    if (units.length === 0) {
        return null;
    }

    return unitDestination(units[0].unitId, columns, rooms);
};

export const KanbanBookings: React.FC<KanbanBookingsProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { getOrganizationMarketplace, isUserLoggedInMarketplace, organizationServicesInfo } =
        useOrganizationMarketplaces();

    useEffect(() => {
        if (organizationServicesInfo.length > 0) {
            return;
        }
        dispatch(fetchOrganizationServicesInfoThunk(apiClientWrapper));
        // The client wrapper changes every render; this only needs to run until the catalog is loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationServicesInfo.length]);
    const columns = useMemo(() => {
        const allowedMarketplaceIds = new Set(props.selectedMarketplaceIds);
        const appointments =
            allowedMarketplaceIds.size === 0
                ? props.appointments
                : props.appointments.filter(appointment => allowedMarketplaceIds.has(appointment.marketplaceId));
        const grouped = groupDaycareAppointments(appointments);
        const knownRoomIds = new Set(grouped.flatMap(column => (column.roomId == null ? [] : [column.roomId])));
        const selectedUnitIds = new Set(props.selectedRoomUnitIds);
        const extras: Array<KanbanRoomColumn> = [];

        organizationServicesInfo.forEach(info => {
            if (allowedMarketplaceIds.size > 0 && !allowedMarketplaceIds.has(info.marketplace.id)) {
                return;
            }
            const rooms = [...(info.rooms?.daycare ?? []), ...(info.rooms?.boarding ?? [])];
            const byId = new Map(rooms.map(room => [room.id, room]));
            byId.forEach(room => {
                if (knownRoomIds.has(room.id)) {
                    return;
                }
                const units = (room.roomUnits ?? [])
                    .filter(unit => selectedUnitIds.size === 0 || selectedUnitIds.has(unit.id))
                    .map(unit => ({
                        key: `unit-${unit.id}`,
                        unitId: unit.id,
                        title: unit.name,
                        appointments: [] as Array<AppointmentListItem>
                    }))
                    .sort((a, b) => a.title.localeCompare(b.title));
                if (selectedUnitIds.size > 0 && units.length === 0) {
                    return;
                }
                extras.push({
                    key: `room-${room.id}`,
                    roomId: room.id,
                    title: room.name,
                    units,
                    appointments: [],
                    locationName: info.marketplace.name
                });
                knownRoomIds.add(room.id);
            });
        });

        const unassigned = grouped.filter(column => column.roomId == null);
        const roomColumns = [...grouped.filter(column => column.roomId != null), ...extras].sort((a, b) =>
            a.title.localeCompare(b.title)
        );
        return [...unassigned, ...roomColumns];
    }, [organizationServicesInfo, props.appointments, props.selectedMarketplaceIds, props.selectedRoomUnitIds]);
    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const [unitIdsByRoom, setUnitIdsByRoom] = useState<Record<number, Array<number>>>({});
    const [statusFilterByGroup, setStatusFilterByGroup] = useState<Record<string, number | null>>({});
    const [unitMove, setUnitMove] = useState<{
        unitKey: string;
        top?: number;
        bottom?: number;
        right: number;
        maxHeight: number;
    } | null>(null);

    const unitsOfRoom = (roomId: number, columnUnits: Array<KanbanUnitGroup>) => {
        const names = new Map<number, string>();
        rooms.page
            .find(room => room.id === roomId)
            ?.units.forEach(unit => {
                if (unit.id != null) {
                    names.set(unit.id, unit.name);
                }
            });
        organizationServicesInfo.forEach(info => {
            [...(info.rooms?.daycare ?? []), ...(info.rooms?.boarding ?? [])]
                .filter(room => room.id === roomId)
                .forEach(room => {
                    room.roomUnits?.forEach(unit => names.set(unit.id, unit.name));
                });
        });
        columnUnits.forEach(unit => names.set(unit.unitId, unit.title));
        return Array.from(names, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    };

    const unitsToShow = (column: KanbanRoomColumn) => {
        if (column.roomId == null) {
            return column.units;
        }
        const selected = unitIdsByRoom[column.roomId] ?? [];
        if (selected.length === 0) {
            return column.units;
        }
        const namedUnits = unitsOfRoom(column.roomId, column.units);
        return selected.flatMap(unitId => {
            const existing = column.units.find(unit => unit.unitId === unitId);
            if (existing) {
                return [existing];
            }
            const name = namedUnits.find(unit => unit.id === unitId)?.name;
            if (!name) {
                return [];
            }
            return [
                {
                    key: `unit-${unitId}`,
                    unitId,
                    title: name,
                    appointments: [] as Array<AppointmentListItem>
                }
            ];
        });
    };

    const locationLabel = (column: KanbanRoomColumn) => {
        const names = Array.from(
            new Set(
                column.appointments
                    .map(appointment => getOrganizationMarketplace(appointment.marketplaceId)?.name)
                    .filter((name): name is string => Boolean(name))
            )
        );
        if (names.length === 1) {
            return names[0];
        }
        return names.length === 0 ? column.locationName ?? 'All Locations' : 'All Locations';
    };

    const belongsToCurrentLocation = (appointment: AppointmentListItem) =>
        isUserLoggedInMarketplace(appointment.marketplaceId);

    const currentLocationRoomIds = useMemo(() => {
        const servicesInfo = organizationServicesInfo.find(info => isUserLoggedInMarketplace(info.marketplace.id));
        return new Set(
            [...(servicesInfo?.rooms.daycare ?? []), ...(servicesInfo?.rooms.boarding ?? [])].map(room => room.id)
        );
    }, [organizationServicesInfo, isUserLoggedInMarketplace]);

    const columnAcceptsDrop = (column: KanbanRoomColumn) => {
        if (column.roomId == null) {
            return true;
        }
        if (column.appointments.some(belongsToCurrentLocation)) {
            return true;
        }
        if (column.appointments.length > 0) {
            return false;
        }
        return currentLocationRoomIds.has(column.roomId);
    };

    const renderCard = (appointment: AppointmentListItem, columnId: string, rowId?: string) => {
        const locked = !belongsToCurrentLocation(appointment);
        return (
            <KanbanBoardCard
                id={String(appointment.appointmentId)}
                key={appointment.appointmentId}
                columnId={columnId}
                rowId={rowId}
                draggable={!locked}
            >
                <KanbanAppointmentCard
                    appointment={appointment}
                    selected={props.selectedIds.has(appointment.appointmentId)}
                    disabled={locked}
                    scrollerContainerId={DAYCARE_KANBAN_SCROLLER_ID}
                    onToggleSelect={checked => props.onToggleSelect(appointment.appointmentId, checked)}
                    onShowOrder={() => props.onShowOrder(appointment.bookingId, appointment.orderId)}
                    onShowPet={locked ? undefined : () => props.onShowPet(appointment.pet.id)}
                />
            </KanbanBoardCard>
        );
    };

    const dropCardHandler = useCallback(
        (event: KanbanCardMoveEvent) => {
            const dragged = props.appointments.find(appointment => appointment.appointmentId === Number(event.cardId));
            if (!dragged || !belongsToCurrentLocation(dragged)) {
                return;
            }

            const destination = destinationFor(event.to, columns, rooms.page, roomColumn => {
                if (roomColumn.roomId == null) {
                    return [];
                }
                const selected = unitIdsByRoom[roomColumn.roomId] ?? [];
                return roomColumn.units.filter(unit => selected.length === 0 || selected.includes(unit.unitId));
            });
            if (!destination) {
                return;
            }

            if (destination.roomUnitId != null) {
                const destinationColumn = columns.find(column =>
                    column.units.some(unit => unit.unitId === destination.roomUnitId)
                );
                if (!destinationColumn || !columnAcceptsDrop(destinationColumn)) {
                    return;
                }
            }

            const selectedIds = props.appointments
                .filter(
                    appointment =>
                        props.selectedIds.has(appointment.appointmentId) && belongsToCurrentLocation(appointment)
                )
                .map(appointment => appointment.appointmentId);
            const movingIds =
                selectedIds.length > 0
                    ? Array.from(new Set([...selectedIds, dragged.appointmentId]))
                    : [dragged.appointmentId];
            const moving = movingIds
                .map(id => props.appointments.find(appointment => appointment.appointmentId === id))
                .filter((appointment): appointment is AppointmentListItem => appointment != null)
                .filter(appointment => currentUnitId(appointment) !== destination.roomUnitId);

            if (moving.length === 0) {
                return;
            }

            setUnitMove(null);
            props.onDropAppointments(
                moving.map(appointment => appointment.appointmentId),
                destination.roomUnitId,
                destination.label
            );
        },
        [belongsToCurrentLocation, columnAcceptsDrop, columns, props, rooms.page, unitIdsByRoom]
    );
    const dropCardHandlerRef = useRef(dropCardHandler);
    dropCardHandlerRef.current = dropCardHandler;
    const moveCardHandler = useCallback((event: KanbanCardMoveEvent) => {
        dropCardHandlerRef.current(event);
    }, []);

    const renderSelection = (groupKey: string, appointments: Array<AppointmentListItem>, locked = false) => (
        <KanbanColumnSelection
            appointments={appointments}
            visibleAppointments={visibleAppointments(appointments, statusFilterByGroup[groupKey] ?? null).filter(
                belongsToCurrentLocation
            )}
            selectedIds={props.selectedIds}
            statusId={statusFilterByGroup[groupKey] ?? null}
            disabled={locked}
            onToggleSelectAll={props.onToggleSelectAll}
            onSelectStatus={statusId => setStatusFilterByGroup(prev => ({ ...prev, [groupKey]: statusId }))}
        />
    );

    return (
        <div className={classes.root}>
            {props.loading ? (
                <div className={classes.loading}>
                    <Spinner />
                </div>
            ) : (
                <KanbanBoard
                    className={classes.board}
                    onCardMove={moveCardHandler}
                >
                    {columns.map(column => {
                        const unassignedVisible = visibleAppointments(
                            column.appointments,
                            statusFilterByGroup[column.key] ?? null
                        );
                        const canDropOnColumn = columnAcceptsDrop(column);

                        return (
                            <KanbanBoardColumn
                                key={column.key}
                                id={column.key}
                                acceptsDrop={canDropOnColumn}
                                title={column.title}
                                count={
                                    column.roomId == null
                                        ? unassignedVisible.length
                                        : column.units
                                              .filter(
                                                  unit =>
                                                      (unitIdsByRoom[column.roomId!] ?? []).length === 0 ||
                                                      (unitIdsByRoom[column.roomId!] ?? []).includes(unit.unitId)
                                              )
                                              .reduce((total, unit) => total + unit.appointments.length, 0)
                                }
                                subtitle={
                                    <>
                                        <FontAwesomeIcon icon={faLocationDot} />
                                        <span>{locationLabel(column)}</span>
                                    </>
                                }
                                filter={
                                    column.roomId != null && unitsOfRoom(column.roomId, column.units).length > 0 ? (
                                        <KanbanRoomUnitsFilter
                                            units={unitsOfRoom(column.roomId, column.units)}
                                            selectedUnitIds={unitIdsByRoom[column.roomId] ?? []}
                                            onChange={unitIds =>
                                                setUnitIdsByRoom(prev => ({
                                                    ...prev,
                                                    [column.roomId!]: unitIds
                                                }))
                                            }
                                        />
                                    ) : undefined
                                }
                            >
                                {column.roomId == null && (
                                    <div
                                        className={classes.selectionBar}
                                        data-selection-bar
                                    >
                                        {renderSelection(
                                            column.key,
                                            column.appointments,
                                            column.appointments.length > 0 &&
                                                column.appointments.every(
                                                    appointment => !belongsToCurrentLocation(appointment)
                                                )
                                        )}
                                    </div>
                                )}
                                {column.roomId == null && column.appointments.length === 0 && (
                                    <KanbanBoardEmptyColumn />
                                )}
                                {column.roomId == null &&
                                    unassignedVisible.map(appointment => renderCard(appointment, column.key))}
                                {unitsToShow(column).map(unit => {
                                    const unitVisible = visibleAppointments(
                                        unit.appointments,
                                        statusFilterByGroup[unit.key] ?? null
                                    );
                                    const unitLocked =
                                        !canDropOnColumn ||
                                        (unit.appointments.length > 0 &&
                                            unit.appointments.every(
                                                appointment => !belongsToCurrentLocation(appointment)
                                            ));
                                    return (
                                        <KanbanBoardRow
                                            key={unit.key}
                                            id={unit.key}
                                            columnId={column.key}
                                            acceptsDrop={canDropOnColumn}
                                            title={unit.title}
                                            count={unitVisible.length}
                                            headerStart={renderSelection(unit.key, unit.appointments, unitLocked)}
                                            headerEnd={
                                                <button
                                                    type="button"
                                                    className={classes.unitMove}
                                                    aria-label="Move pets between rooms"
                                                    disabled={unitLocked || unitVisible.length === 0}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        if (unitLocked) {
                                                            return;
                                                        }
                                                        const appointmentIds = unitVisible
                                                            .filter(belongsToCurrentLocation)
                                                            .map(appointment => appointment.appointmentId);
                                                        if (appointmentIds.length === 0) {
                                                            return;
                                                        }
                                                        if (unitMove?.unitKey === unit.key) {
                                                            setUnitMove(null);
                                                            return;
                                                        }
                                                        const rect = event.currentTarget.getBoundingClientRect();
                                                        const gap = 8;
                                                        const margin = 16;
                                                        const spaceBelow =
                                                            window.innerHeight - rect.bottom - gap - margin;
                                                        const spaceAbove = rect.top - gap - margin;
                                                        const openAbove = spaceBelow < 280 && spaceAbove > spaceBelow;
                                                        props.onMoveUnit(appointmentIds);
                                                        setUnitMove({
                                                            unitKey: unit.key,
                                                            top: openAbove ? undefined : rect.bottom + gap,
                                                            bottom: openAbove
                                                                ? window.innerHeight - rect.top + gap
                                                                : undefined,
                                                            right: Math.max(margin, window.innerWidth - rect.right),
                                                            maxHeight: Math.min(
                                                                420,
                                                                Math.max(160, openAbove ? spaceAbove : spaceBelow)
                                                            )
                                                        });
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                                                </button>
                                            }
                                        >
                                            {unitVisible.map(appointment =>
                                                renderCard(appointment, column.key, unit.key)
                                            )}
                                        </KanbanBoardRow>
                                    );
                                })}
                            </KanbanBoardColumn>
                        );
                    })}
                </KanbanBoard>
            )}
            {unitMove && (
                <KanbanMoveDestinationMenu
                    appointments={props.occupancyAppointments ?? props.appointments}
                    anchor={{
                        top: unitMove.top,
                        bottom: unitMove.bottom,
                        right: unitMove.right,
                        maxHeight: unitMove.maxHeight
                    }}
                    onClose={() => setUnitMove(null)}
                    onSelect={(roomUnitId, label) => {
                        setUnitMove(null);
                        props.onSelectMoveDestination(roomUnitId, label);
                    }}
                />
            )}
        </div>
    );
};
